/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — product page behaviour
   Sticky media stage + Product information tabs.

   Deliberately standalone from cnc-app.js. That file still owns the variant
   pickers, the quantity stepper, the wishlist heart and the Buy Now handoff on
   this page — all of which key off attributes this section keeps rendering. The
   gallery here uses its own `data-stage-*` names precisely so cnc-app.js's
   older `initGallery` never binds to it: that one assumed a vertical mosaic on
   desktop and scrolls the PAGE to reach a photo, which is the opposite of what
   a stage that is supposed to stay put should do.

   Everything below is progressive: with scripting off, every panel is in flow
   under its own heading and the gallery is a plain swipe carousel.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var finePointer  = window.matchMedia("(hover: hover) and (pointer: fine)");

  function once(el, flag) {
    if (!el || el.dataset[flag] === "1") return false;
    el.dataset[flag] = "1";
    return true;
  }

  function behavior() { return reduceMotion ? "auto" : "smooth"; }

  /* ═══ Sticky offset ═══════════════════════════════════════════════════════
     The stage parks below whatever the host theme pins to the top of the
     window. Measured rather than hard-coded, because the header's height moves
     with the announcement bar, the logo size and the breakpoint — and a guessed
     number is wrong on every store but the one it was guessed on. */

  var HEADER_HINTS = [
    "#shopify-section-header", ".shopify-section-header", "sticky-header",
    "header.header", ".site-header", ".header-wrapper", "header[role='banner']",
    ".cnc-header", "#shopify-section-announcement-bar"
  ].join(",");

  function pinnedHeight() {
    var best = 0;
    var nodes;
    try { nodes = document.querySelectorAll(HEADER_HINTS); }
    catch (e) { return 0; }

    Array.prototype.forEach.call(nodes, function (el) {
      var cs = window.getComputedStyle(el);
      if (cs.position !== "fixed" && cs.position !== "sticky") return;
      if (cs.display === "none" || cs.visibility === "hidden") return;
      var r = el.getBoundingClientRect();
      // Anything not actually pinned near the top, or absurdly tall, is some
      // other sticky thing (a cart drawer, a promo rail) and not our ceiling.
      if (r.height < 24 || r.height > 200) return;
      if (r.top > 8) return;
      best = Math.max(best, r.bottom);
    });
    return Math.max(0, Math.round(best));
  }

  function syncStickyTop() {
    var pdps = document.querySelectorAll(".cnc-pdp");
    if (!pdps.length) return;
    var top = pinnedHeight() + 16;
    Array.prototype.forEach.call(pdps, function (el) {
      el.style.setProperty("--pdp-top", top + "px");
    });
  }

  /* ═══ Media stage ═════════════════════════════════════════════════════════ */

  function initStage(root) {
    if (!once(root, "cncStage")) return;

    var frame  = root.querySelector("[data-stage-frame]");
    var track  = root.querySelector("[data-stage-track]");
    if (!frame || !track) return;

    var slides = Array.prototype.slice.call(track.children);
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-stage-thumb]"));
    var dots   = Array.prototype.slice.call(root.querySelectorAll("[data-stage-dot]"));
    var navs   = Array.prototype.slice.call(root.querySelectorAll("[data-stage-nav]"));
    var count  = root.querySelector("[data-stage-count]");
    var index  = 0;

    if (slides.length < 2) {
      navs.forEach(function (b) { b.hidden = true; });
      if (count) count.hidden = true;
    }

    function paint(i) {
      index = i;
      slides.forEach(function (s, n) { s.classList.toggle("is-current", n === i); });
      thumbs.forEach(function (t, n) {
        var on = n === i;
        t.classList.toggle("is-active", on);
        t.setAttribute("aria-current", on ? "true" : "false");
      });
      dots.forEach(function (d, n) { d.classList.toggle("is-on", n === i); });
      if (count) count.textContent = (i + 1) + " / " + slides.length;
      navs.forEach(function (b) {
        var dir = parseInt(b.dataset.stageNav, 10);
        b.disabled = dir < 0 ? i === 0 : i === slides.length - 1;
      });

      // Keep the active thumb in view without dragging the page along with it.
      var thumb = thumbs[i];
      if (!thumb) return;
      var rail = thumb.parentNode;
      var vertical = rail.scrollHeight > rail.clientHeight + 4;
      if (vertical) {
        var wantTop = thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2;
        rail.scrollTo({ top: wantTop, behavior: behavior() });
      } else if (rail.scrollWidth > rail.clientWidth + 4) {
        var wantLeft = thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2;
        rail.scrollTo({ left: wantLeft, behavior: behavior() });
      }
    }

    function goTo(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      var slide = slides[i];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: behavior() });
      paint(i);
    }

    /* The scroll listener is the single source of truth for which photo is
       showing — a swipe, a thumb click and an arrow all end as a scroll, so
       there is one place that decides and no two states to keep in step. */
    var ticking = false;
    track.addEventListener("scroll", function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var w = track.clientWidth || 1;
        var i = Math.round(track.scrollLeft / w);
        if (i !== index) paint(Math.max(0, Math.min(slides.length - 1, i)));
      });
    }, { passive: true });

    thumbs.forEach(function (t, i) {
      t.addEventListener("click", function () { goTo(i); });
    });
    navs.forEach(function (b) {
      b.addEventListener("click", function () {
        goTo(index + parseInt(b.dataset.stageNav, 10));
      });
    });

    frame.addEventListener("keydown", function (e) {
      if (e.key === "ArrowRight") { e.preventDefault(); goTo(index + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goTo(index - 1); }
    });

    /* ── Cursor-follow magnify ──────────────────────────────────────────────
       Bound only on a fine pointer. On touch the browser's own pinch is the
       zoom, and a hover class there latches on first tap and never clears. */
    if (frame.hasAttribute("data-stage-zoom") && finePointer.matches && !reduceMotion) {
      frame.classList.add("is-zoomable");

      frame.addEventListener("mouseenter", function () { frame.classList.add("is-zooming"); });
      frame.addEventListener("mouseleave", function () {
        frame.classList.remove("is-zooming");
        var img = slides[index] && slides[index].querySelector("img");
        if (img) img.style.transformOrigin = "center";
      });
      frame.addEventListener("mousemove", function (e) {
        var img = slides[index] && slides[index].querySelector("img");
        if (!img) return;
        var r = frame.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x.toFixed(2) + "% " + y.toFixed(2) + "%";
      });
    }

    paint(0);

    // Handed to the option sync below so choosing a colour can move the stage.
    root.__cncGoTo = goTo;
  }

  /* ═══ Options → photo, and options → label ════════════════════════════════
     cnc-app.js already resolves which variant a set of chosen options means and
     writes its id into the hidden field. Rather than repeat that matching here
     — two copies of the same rule is two chances to disagree — this reads the
     ANSWER out of the field once the click has been handled, and uses it to
     look up the photo and to correct the "COLOUR : x" heading, neither of which
     cnc-app.js touches.

     The deferred read is the whole trick: both listeners sit on the same form,
     so this one has to run after the one that updates the field. A timeout of 0
     is enough and does not care which script bound first. */

  function initOptionSync(form) {
    if (!once(form, "cncOptSync")) return;

    var pdp = form.closest(".cnc-pdp");
    var stage = pdp && pdp.querySelector("[data-stage]");
    var idField = form.querySelector("[name='id']");
    if (!idField) return;

    var media = {};
    if (stage) {
      try { media = JSON.parse(stage.getAttribute("data-variant-media") || "{}"); }
      catch (e) { media = {}; }
    }

    var lastMedia = -1;
    var discEl = form.querySelector("[data-disc]");
    var badge  = stage && stage.querySelector("[data-stage-badge]");

    // Mirrored, not recomputed. cnc-app.js already decides whether the chosen
    // variant is discounted and by how much; the badge on the photo just says
    // the same thing in the same words, so there is one rule and not two.
    function syncBadge() {
      if (!badge || !discEl) return;
      badge.hidden = discEl.hidden;
      if (!discEl.hidden) badge.textContent = (discEl.textContent || "").replace(/OFF/i, "off").trim();
    }

    form.addEventListener("click", function (e) {
      var opt = e.target.closest("[data-option-index]");
      if (!opt) return;

      window.setTimeout(function () {
        // The heading for THIS option group follows the swatch immediately —
        // it is a property of the click, not of the resolved variant.
        var label = form.querySelector('[data-opt-value="' + opt.dataset.optionIndex + '"]');
        if (label) label.textContent = opt.dataset.optionValue;

        syncBadge();

        if (!stage || !stage.__cncGoTo) return;
        var idx = media[String(idField.value)];
        // Undefined means this variant has no photo of its own. Leaving the
        // stage alone beats yanking it back to photo one.
        if (typeof idx !== "number" || idx === lastMedia) return;
        lastMedia = idx;
        stage.__cncGoTo(idx);
      }, 0);
    });

    /* Mirror once on load, not only on a click.

       cnc-app.js's initVariants() ends by calling apply(), so the "% OFF"
       beside the price is rewritten by JS on every page load, discount or not.
       The badge on the photo was only ever touched by the click handler above,
       so until the customer picked a swatch it kept whatever Liquid printed —
       which is how a product could show 17% on the photo and 16% by the price
       on first paint, before anyone touched anything.

       Deferred for the same reason the click path defers: both files bind to
       this form and the mirror has to read the price line AFTER cnc-app.js has
       written it. A timeout of 0 does not care which script bound first. */
    window.setTimeout(syncBadge, 0);
  }

  /* ═══ Coupon codes ════════════════════════════════════════════════════════
     Its own handler rather than cnc-app.js's initCopy(), which swaps the
     button's entire textContent for "Copied!" — that works on a text button and
     would delete the icon out of this one. */

  function initCoupons(scope) {
    (scope || document).querySelectorAll("[data-cnc-copy]").forEach(function (btn) {
      if (!once(btn, "cncCoupon")) return;

      btn.addEventListener("click", function () {
        var code = btn.getAttribute("data-cnc-copy") || "";
        var label = btn.querySelector("[data-copy-label]");

        var done = function (okay) {
          btn.classList.toggle("is-copied", okay);
          if (label) label.textContent = okay ? "Copied" : "Press ⌘C";
          clearTimeout(btn.__revert);
          btn.__revert = setTimeout(function () {
            btn.classList.remove("is-copied");
            if (label) label.textContent = "Copy";
          }, 2200);
        };

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code).then(function () { done(true); },
                                                   function () { fallback(code, done); });
        } else {
          fallback(code, done);
        }
      });
    });
  }

  /* Older Safari and any non-HTTPS preview have no async clipboard. */
  function fallback(code, done) {
    var ta = document.createElement("textarea");
    ta.value = code;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:-9999px;opacity:0;";
    document.body.appendChild(ta);
    ta.select();
    var okay = false;
    try { okay = document.execCommand("copy"); } catch (e) { okay = false; }
    document.body.removeChild(ta);
    done(okay);
  }

  /* ═══ Best offers ═════════════════════════════════════════════════════════
     The Save Extra card used to print the editor's Offer chip blocks in the
     order they were dragged — the same codes on every product, whether or not
     they could ever apply. It now answers the question a shopper actually has:
     "what's the lowest I can pay for THIS, and do I have to do anything?"

     ONE BAG, ONE NUMBER. Every code is valued against the bag the customer
     WILL have — what's in it now plus this variant × quantity — which is
     exactly what the cart judges it against after Add to Bag. Ranking on the
     product alone let the card promise SAVE100 while the cart, looking at the
     whole bag, kept 15% off; the card and the cart must never disagree.

     ONE COUPON PER ORDER. Codes don't stack, so the card has two modes:

       Bag has no coupon   The best code is flagged BEST and "Auto-applies":
                           after Add to Bag lands (or just before Buy Now
                           leaves for checkout) it goes on the cart by itself.
                           Runner-ups get Apply, or Copy while still locked.

       Bag has a coupon    Nothing is applied automatically — ever — whoever
                           put that code there. It leads the card as "Applied
                           in your bag", priced with this product added. Only a
                           code that saves MORE on that bag is offered, as an
                           explicit one-tap Switch that says how much more.
                           No better code → the card just confirms the one
                           they have.

     Also: a code we applied and the customer removed is never re-applied this
     session; a refusal from Shopify (not combinable, expired) is rolled back
     silently after one runner-up try. Everything fails soft to the
     server-rendered editor blocks. */

  var OFFER_STORE = "cncBestOffer";

  function offerRec() {
    try { return JSON.parse(sessionStorage.getItem(OFFER_STORE) || "{}") || {}; }
    catch (e) { return {}; }
  }
  function offerSave(rec) {
    try { sessionStorage.setItem(OFFER_STORE, JSON.stringify(rec)); } catch (e) {}
  }

  /* Paise are kept when there are any, exactly like money() in cnc-app.js.
     This used to round to whole rupees, so the card promised "Best Price ₹212"
     while the drawer, the /cart page and Shopify's checkout all said ₹211.65 —
     the same bag, two prices. Checkout's figure cannot be restyled, so every
     surface before it matches checkout, not the other way round. */
  function inr(rs) {
    var n = Math.round(Math.abs(rs || 0) * 100) / 100;
    var parts = (Number.isInteger(n) ? String(n) : n.toFixed(2)).split(".");
    var x = parts[0], last3 = x.slice(-3), rest = x.slice(0, -3);
    if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
    return "₹" + last3 + (parts[1] ? "." + parts[1] : "");
  }
  function escHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function readJson(root, sel, dflt) {
    var el = root.querySelector(sel);
    if (!el) return dflt;
    try { return JSON.parse(el.textContent); } catch (e) { return dflt; }
  }
  function codesOn(cart) {
    return ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code || "").toUpperCase(); });
  }
  function isPts(code) { return /^CNCPTS-/i.test(String(code || "")); }
  /** The coupon Shopify is honouring on the bag right now — never points. */
  function bagCode(cart) {
    var list = (cart && cart.discount_codes) || [];
    for (var i = 0; i < list.length; i++) {
      if (!isPts(list[i].code) && list[i].applicable) return String(list[i].code).toUpperCase();
    }
    return "";
  }

  /* What one coupon is worth on the projected bag.
       ctx.unit / ctx.qty   this product, in rupees
       ctx.bag              bag subtotal INCLUDING this line (before coupons)
       ctx.bagQty           items in the bag INCLUDING this line
     `save` is the rupee value on the whole bag — the ranking number, and the
     same one the cart will produce. `pay` is this product's line with its
     share of the code taken off — the headline shoppers read. */
  function valueOffer(c, ctx) {
    var line = ctx.unit * ctx.qty;
    var o = { c: c, code: c.code ? String(c.code).toUpperCase() : "", save: 0, ok: true, need: "", prog: 1 };

    if (c.type === "auto") {
      // Bundle deals are collections named 2-dog-toys-for-749, applied by
      // Shopify itself. Only offer one on a product that's in that collection.
      if (!c.bundleQty || !c.bundlePrice || ctx.collections.indexOf(String(c.id)) === -1) return null;
      var per = c.bundleQty * ctx.unit - c.bundlePrice;
      if (per <= 0) return null;
      o.auto = true;
      o.ok = ctx.qty >= c.bundleQty;
      o.save = o.ok ? Math.floor(ctx.qty / c.bundleQty) * per : per;
      if (!o.ok) {
        var more = c.bundleQty - ctx.qty;
        o.need = "Add " + more + " more to unlock";
        o.prog = ctx.qty / c.bundleQty;
      }
      o.gain = o.ok
        ? "You save <b>" + inr(o.save) + "</b> on this bundle"
        : "Save <b>" + inr(per) + "</b> when you buy " + c.bundleQty;
      o.badge = c.bundleQty + " for " + inr(c.bundlePrice);
      o.icon = "layers";
      o.title = c.label || ("Buy " + c.bundleQty + " for " + inr(c.bundlePrice));
      return o;
    }

    if (!o.code) return null;

    var lineOff = 0;
    if (c.freeShip) {
      o.ship = true;
      o.badge = "FREE";
      o.icon = "truck";
    } else if (c.pct) {
      o.save = ctx.bag * c.pct / 100;
      if (c.maxDiscount) o.save = Math.min(o.save, c.maxDiscount);
      lineOff = ctx.bag > 0 ? o.save * line / ctx.bag : 0;
      o.badge = "-" + c.pct + "%";
      o.icon = "percent";
    } else if (c.flat) {
      o.save = Math.min(c.flat, ctx.bag);
      // Shopify spreads a flat order discount across the lines pro rata.
      lineOff = ctx.bag > 0 ? o.save * line / ctx.bag : 0;
      o.badge = inr(c.flat);
      o.icon = "percent";
    } else {
      return null;
    }

    if (c.minOrder && ctx.bag < c.minOrder) {
      o.ok = false;
      o.need = "Add " + inr(Math.ceil(c.minOrder - ctx.bag)) + " more to unlock";
      o.prog = ctx.bag / c.minOrder;
    } else if (c.minQty && ctx.bagQty < c.minQty) {
      o.ok = false;
      var n = c.minQty - ctx.bagQty;
      o.need = "Add " + n + " more item" + (n !== 1 ? "s" : "") + " to unlock";
      o.prog = ctx.bagQty / c.minQty;
    }

    if (o.ship) {
      o.gain = "Free delivery on this order";
    } else {
      o.pay = Math.max(0, line - lineOff);
      o.gain = (ctx.qty > 1 ? "<b>" + inr(o.pay) + "</b> for " + ctx.qty : "Get it for <b>" + inr(o.pay) + "</b>")
        + ' <span class="cnc-coupon-save">save ' + inr(o.save) + (ctx.solo ? "" : " on bag") + "</span>";
    }
    return o;
  }

  /* Usable today beats locked; within each, biggest rupee saving on the bag;
     free shipping sorts under money off; a code with no minimum wins a tie. */
  function rankOffers(list) {
    return list.sort(function (a, b) {
      if (a.ok !== b.ok) return a.ok ? -1 : 1;
      if (!!a.ship !== !!b.ship) return a.ship ? 1 : -1;
      if (b.save !== a.save) return b.save - a.save;
      return (a.c.minOrder || 0) - (b.c.minOrder || 0);
    });
  }

  /* The top N: the best usable offer first, then whatever is worth the most
     of the rest — which is how a locked ₹150-off-above-₹999 earns a slot over
     a usable 5%, with a meter showing how close the customer is. */
  function pickTop(ranked, n) {
    if (!ranked.length) return [];
    var rest = ranked.slice(1).sort(function (a, b) {
      var av = a.ok ? a.save : a.save * 0.85, bv = b.ok ? b.save : b.save * 0.85;
      return bv - av;
    });
    return [ranked[0]].concat(rest).slice(0, n);
  }

  /* Leave the skeleton. `reveal` = nothing live to show, so fall back to the
     editor's blocks — or hide the card if there are none. */
  function offersReady(root, reveal) {
    root.classList.remove("is-loading");
    root.setAttribute("aria-busy", "false");
    if (reveal && !root.querySelector("[data-offers-track] .cnc-coupon")) root.hidden = true;
  }

  /* The coupon list changes when the merchant edits a discount, not between
     two product pages. Keeping the last copy for the tab's session means the
     second product a customer opens paints its offers instantly; the network
     copy still arrives and repaints if anything moved. */
  var COUPON_CACHE = "cncCouponsCache";
  var COUPON_CACHE_MS = 15 * 60 * 1000;
  function cachedCoupons() {
    try {
      var c = JSON.parse(sessionStorage.getItem(COUPON_CACHE) || "null");
      if (c && Array.isArray(c.list) && c.list.length && Date.now() - c.at < COUPON_CACHE_MS) return c.list;
    } catch (e) {}
    return null;
  }
  function cacheCoupons(list) {
    try { sessionStorage.setItem(COUPON_CACHE, JSON.stringify({ at: Date.now(), list: list })); } catch (e) {}
  }

  function initBestOffers(root) {
    if (!once(root, "cncBestOffers")) return;
    if (root.getAttribute("data-live") !== "true") return;
    if (!window.CNC || typeof window.CNC.coupons !== "function") { offersReady(root, true); return; }

    var form = root.closest("form") || document.querySelector("[data-product-form]");
    var track = root.querySelector("[data-offers-track]");
    var countEl = root.querySelector("[data-offers-count]");
    var noteEl = root.querySelector("[data-offers-note]");
    var limit = parseInt(root.getAttribute("data-limit"), 10) || 2;
    var autoOn = root.getAttribute("data-auto-apply") === "true";
    var collections = (root.getAttribute("data-collections") || "").split(",").filter(Boolean);
    var prices = readJson(root, "[data-offer-prices]", {});
    var blocks = readJson(root, "[data-offer-blocks]", []);
    var tpl = root.querySelector("[data-offer-icons]");
    var pill = document.querySelector("[data-cnc-bestprice]");
    var pillText = pill && pill.querySelector("[data-bestprice-text]");

    var copyFor = {};
    blocks.forEach(function (b) { if (b && b.code) copyFor[String(b.code).toUpperCase()] = b; });

    var S = { coupons: null, cart: null, chosen: offerRec().chosen || "", busy: false, error: "" };

    function icon(name) {
      if (!tpl) return "";
      var el = tpl.content.querySelector('[data-i="' + name + '"]');
      return el ? el.innerHTML : "";
    }
    function variantId() {
      var i = form && form.querySelector('input[name="id"]');
      return i ? String(i.value) : "";
    }
    function qty() {
      var i = form && form.querySelector('input[name="quantity"]');
      return Math.max(1, parseInt(i && i.value, 10) || 1);
    }
    function unitRs() {
      var p = prices[variantId()];
      if (p == null) p = parseInt(root.getAttribute("data-price"), 10) || 0;
      return p / 100;
    }

    /* The bag after this add. Pre-coupon prices (original_line_price), since a
       minimum order and a percentage are both judged before the code. This
       variant's existing line is added to, not replaced — Add to Bag adds. */
    function context() {
      var cart = S.cart, vid = variantId(), other = 0, otherQty = 0, mine = 0, mineQty = 0;
      ((cart && cart.items) || []).forEach(function (it) {
        var v = (it.original_line_price != null ? it.original_line_price : it.line_price) / 100;
        if (String(it.variant_id) === vid) { mine += v; mineQty += it.quantity; return; }
        other += v;
        otherQty += it.quantity;
      });
      var u = unitRs(), q = qty();
      /* Already in the bag: price the bag as it IS. Projecting another add on
         top made the card double-count the product the moment Add to Bag
         landed — "save ₹225 on bag" for a bag that only saves ₹112. */
      var line = mineQty ? mine : u * q;
      var lineQty = mineQty || q;
      return {
        unit: u, qty: lineQty, bag: line + other, bagQty: lineQty + otherQty,
        solo: otherQty === 0, collections: collections
      };
    }

    function evaluate() {
      if (!S.coupons) return [];
      var ctx = context();
      return rankOffers(S.coupons.map(function (c) { return valueOffer(c, ctx); }).filter(Boolean));
    }

    function usable(ranked) {
      return ranked.filter(function (o) { return o.ok && o.code && !o.ship; });
    }

    /* The code that WILL go on an empty-coupon bag: the customer's pick if it
       is usable, otherwise the best usable money-off code. */
    function target(ranked) {
      var list = usable(ranked);
      if (S.chosen) {
        var pick = list.filter(function (o) { return o.code === S.chosen; })[0];
        if (pick) return pick;
      }
      return list[0] || null;
    }

    function ticket(o, opt) {
      var cls = "cnc-coupon"
        + (o.auto ? " is-auto" : "")
        + (opt.flag ? " is-best" : "")
        + (!o.ok ? " is-locked" : "")
        + (opt.applied ? " is-applied" : "");

      var action;
      if (opt.applied) {
        action = '<span class="cnc-coupon-state is-on">' + icon("check-circle") + " In your bag</span>";
      } else if (o.auto) {
        action = '<span class="cnc-coupon-auto">' + icon("check-circle") + " Auto</span>";
      } else if (!o.ok) {
        action = '<button type="button" class="cnc-coupon-copy" data-cnc-copy="' + escHtml(o.code) + '"'
          + ' aria-label="Copy coupon code ' + escHtml(o.code) + '">'
          + '<span class="ic-copy">' + icon("copy") + '</span><span class="ic-done">' + icon("check") + "</span>"
          + "<span data-copy-label>Copy</span></button>";
      } else if (opt.autoApplies) {
        action = '<span class="cnc-coupon-state">' + icon("zap") + " Auto-applies</span>";
      } else {
        var verb = opt.switchFrom ? "Switch" : "Apply";
        action = '<button type="button" class="cnc-coupon-apply" data-offer-apply="' + escHtml(o.code) + '"'
          + ' aria-label="' + verb + " to " + escHtml(o.code)
          + (opt.switchFrom ? ", replacing " + escHtml(opt.switchFrom) : "") + '">' + verb + "</button>";
      }

      var merchant = copyFor[o.code];
      var desc = (merchant && merchant.desc) || o.c.desc || "";

      var sub;
      if (!o.ok) {
        sub = '<p class="cnc-coupon-need">' + icon("lock") + " " + escHtml(o.need)
          + (opt.moreBy ? " · saves " + inr(opt.moreBy) + " more" : "") + "</p>"
          + '<span class="cnc-coupon-meter" aria-hidden="true"><i style="width:'
          + Math.round(Math.max(0.06, Math.min(1, o.prog)) * 100) + '%"></i></span>';
      } else if (opt.moreBy) {
        sub = '<p class="cnc-coupon-gain">Saves <b>' + inr(opt.moreBy) + " more</b> than "
          + escHtml(opt.switchFrom) + " on your bag</p>";
      } else {
        sub = '<p class="cnc-coupon-gain">' + o.gain + "</p>";
      }

      return '<div class="' + cls + '" data-offer-code="' + escHtml(o.code) + '">'
        + (opt.flag ? '<span class="cnc-coupon-flag">' + escHtml(opt.flag) + "</span>" : "")
        + '<div class="cnc-coupon-stub">' + icon(o.icon) + "<span>" + escHtml(o.badge) + "</span></div>"
        + '<div class="cnc-coupon-body">'
        + '<div class="cnc-coupon-top"><span class="cnc-coupon-code">'
        + escHtml(o.code || o.title) + "</span>" + action + "</div>"
        + sub
        + (desc ? '<p class="cnc-coupon-desc">' + escHtml(desc) + "</p>" : "")
        + "</div></div>";
    }

    /* A code on the bag that the live list doesn't know (a private code, an
       influencer code) still leads the card — we just can't price it. */
    function unknownTicket(code) {
      return {
        c: { desc: "" }, code: code, ok: true, save: 0, badge: "✓", icon: "check",
        gain: "Your discount is on the bag — you'll see it at checkout."
      };
    }

    function paint() {
      S.autoCode = "";
      var ranked = evaluate();
      var inBag = bagCode(S.cart);
      var rec = offerRec();
      var html = [], shown = 0, note = "", count = "";

      if (inBag) {
        /* ── Mode B: the bag already carries a coupon. Confirm it, and only
           offer what beats it — as a Switch the customer chooses. ── */
        var cur = ranked.filter(function (o) { return o.code === inBag; })[0] || unknownTicket(inBag);
        var curSave = cur.save || 0;
        var better = ranked.filter(function (o) {
          return o.code && o !== cur && !o.ship && !o.auto && o.save > curSave + 0.5;
        });
        var alts = pickTop(better, limit - 1).slice(0, limit - 1);

        html.push(ticket(cur, { applied: true }));
        alts.forEach(function (o) {
          html.push(ticket(o, { switchFrom: inBag, moreBy: o.save - curSave }));
        });
        shown = 1 + alts.length;

        var betterNow = alts.filter(function (o) { return o.ok; }).length;
        count = betterNow ? betterNow + " better offer" + (betterNow !== 1 ? "s" : "") : "Best offer applied";
        note = betterNow
          ? "One coupon per order — switching replaces <b>" + escHtml(inBag) + "</b>."
          : "<b>" + escHtml(inBag) + "</b> is your best deal on this bag right now.";
        if (S.error) note = escHtml(S.error);

        if (pill && pillText) {
          if (cur.pay != null && cur.save > 0 && qty() === 1) {
            pillText.innerHTML = "Your price <b>" + inr(cur.pay) + "</b> with " + escHtml(inBag);
            pill.hidden = false;
          } else {
            pill.hidden = true;
          }
        }
      } else {
        /* ── Mode A: no coupon yet. Show the best, and put it on for them. ── */
        if (!ranked.length) {
          if (pill) pill.hidden = true;
          offersReady(root, true);
          return;
        }
        var top = pickTop(ranked, limit);
        var tgt = target(ranked);
        // The auto-apply target always leads, so the BEST flag and the code
        // that actually lands on the cart can never disagree.
        if (tgt && top[0] !== tgt) {
          top = [tgt].concat(top.filter(function (o) { return o !== tgt; })).slice(0, limit);
        }
        var willAuto = autoOn && !rec.declined && !!tgt;
        // Remembered for watchAdd(), which tells the drawer the code is on its
        // way so it doesn't offer "Apply coupon" during the round trip.
        S.autoCode = willAuto && tgt ? tgt.code : "";

        top.forEach(function (o, i) {
          var lead = i === 0 && o.ok;
          html.push(ticket(o, {
            flag: lead ? "Best" : "",
            autoApplies: lead && willAuto && o === tgt
          }));
        });
        shown = top.length;
        count = ranked.length > top.length
          ? "Top " + top.length + " of " + ranked.length
          : ranked.length + " offer" + (ranked.length !== 1 ? "s" : "");

        if (S.error) note = escHtml(S.error);
        else if (willAuto) note = "No code needed — <b>" + escHtml(tgt.code) + "</b> is applied when you add to bag. One coupon per order.";
        else if (tgt) note = "One coupon per order.";

        if (pill && pillText) {
          var money = usable(ranked).filter(function (o) { return o.pay != null && o.save > 0; })[0];
          if (money && qty() === 1) {
            pillText.innerHTML = "Best Price <b>" + inr(money.pay) + "</b> with " + escHtml(money.code);
            pill.hidden = false;
          } else {
            pill.hidden = true;
          }
        }
      }

      track.innerHTML = html.join("");
      root.setAttribute("data-shown", String(shown));
      if (countEl) countEl.textContent = count;
      if (noteEl) { noteEl.innerHTML = note; noteEl.hidden = !note; }
      root.hidden = false;
      offersReady(root, false);
      initCoupons(root);
    }

    /* ── Putting the code on the cart ─────────────────────────────────────
       Only ever onto a bag with NO coupon. Judged on the cart itself, which by
       now holds the product — the same bag paint() priced. */
    function applyTo(cart) {
      if (!cart || !cart.item_count) return Promise.resolve();
      var rec = offerRec();

      // We applied a code and it's gone: the customer removed it. Respect that
      // for the rest of the session.
      if (rec.applied && codesOn(cart).indexOf(rec.applied) === -1 && !bagCode(cart)) {
        rec.declined = true; rec.applied = ""; offerSave(rec);
      }
      if (bagCode(cart)) return Promise.resolve();          // one coupon per order — never swap
      if (rec.declined && !S.chosen) return Promise.resolve();

      var bag = cart.items_subtotal_price / 100;
      ((cart.items) || []).forEach(function (it) {
        // Pre-discount basis, same as paint(): an automatic discount already
        // on a line must not push the bag under a minimum it really meets.
        if (it.original_line_price != null && it.final_line_price != null) {
          bag += (it.original_line_price - it.final_line_price) / 100;
        }
      });
      var ranked = (S.coupons || []).filter(function (c) {
        if (!c.code || c.type === "auto" || c.freeShip) return false;
        if (c.minOrder && bag < c.minOrder) return false;
        if (c.minQty && cart.item_count < c.minQty) return false;
        return true;
      }).map(function (c) {
        var save = c.pct ? bag * c.pct / 100 : Math.min(c.flat || 0, bag);
        if (c.pct && c.maxDiscount) save = Math.min(save, c.maxDiscount);
        return { code: String(c.code).toUpperCase(), save: save };
      }).sort(function (a, b) { return b.save - a.save; });

      if (S.chosen) {
        var pick = ranked.filter(function (r) { return r.code === S.chosen; })[0];
        if (pick) ranked = [pick].concat(ranked.filter(function (r) { return r !== pick; }));
      }
      if (!ranked.length) return Promise.resolve();

      var tries = ranked.slice(0, 2);
      S.busy = true;

      function attempt(i) {
        if (i >= tries.length) {
          // Leave no refused code behind; nothing to show the customer.
          return window.CNC.applyCoupon("").catch(function () {});
        }
        return window.CNC.applyCoupon(tries[i].code).then(function (res) {
          if (res && res.ok) {
            var r = offerRec(); r.applied = tries[i].code; r.declined = false; offerSave(r);
            S.cart = res.cart;
            return;
          }
          return attempt(i + 1);
        });
      }

      return attempt(0)
        .catch(function (e) { console.warn("[cnc-offers] auto-apply skipped —", (e && e.message) || e); })
        .then(function () {
          S.busy = false;
          if (window.CNC.clearCouponPending) window.CNC.clearCouponPending();
          paint();
        });
    }

    function autoApply() {
      if (!autoOn) return Promise.resolve();
      return Promise.all([S.coupons ? S.coupons : window.CNC.coupons(), window.CNC.fetchCart(true)])
        .then(function (res) {
          if (!S.coupons) S.coupons = res[0] || [];
          S.cart = res[1] || S.cart;
          return applyTo(res[1]);
        });
    }

    /* ── Hearing that Add to Bag landed ───────────────────────────────────
       The add belongs to the host theme's <product-form>, which has no event
       we can rely on across theme updates. So: note the variant's quantity in
       the bag at submit, then look at /cart.js until it goes up. A resource
       timing entry for /cart/add makes the first look immediate; the backoff
       is the safety net. Nothing changes = the add failed and nothing is
       applied. */
    function qtyInCart(cart, vid) {
      var n = 0;
      ((cart && cart.items) || []).forEach(function (it) { if (String(it.variant_id) === vid) n += it.quantity; });
      return n;
    }

    function watchAdd() {
      var vid = variantId();
      var before = qtyInCart(S.cart, vid);
      var done = false, timers = [], po = null, landed = false;

      // Only when the bag has no coupon yet — the same gate applyTo() uses.
      if (S.autoCode && !bagCode(S.cart) && window.CNC.markCouponPending) {
        window.CNC.markCouponPending(S.autoCode);
      }

      function stop() {
        if (!done && !landed && window.CNC.clearCouponPending) window.CNC.clearCouponPending();
        done = true;
        timers.forEach(clearTimeout);
        if (po) { try { po.disconnect(); } catch (e) {} }
      }
      function check() {
        if (done) return;
        window.CNC.fetchCart(true).then(function (cart) {
          if (done || !cart) return;
          if (qtyInCart(cart, vid) > before) {
            landed = true;
            stop();
            S.cart = cart;
            autoApply();
          }
        });
      }

      [900, 1800, 3200, 5500].forEach(function (ms) { timers.push(setTimeout(check, ms)); });
      timers.push(setTimeout(stop, 6000));

      if (window.PerformanceObserver) {
        try {
          po = new PerformanceObserver(function (list) {
            list.getEntries().forEach(function (en) {
              if (/\/cart\/add/.test(en.name)) setTimeout(check, 150);
            });
          });
          po.observe({ type: "resource", buffered: false });
        } catch (e) { po = null; }
      }
    }

    if (form) {
      // Bubble phase: the theme's product-form has already taken the submit.
      // Buy Now never reaches here — cnc-app.js stops it in capture — and is
      // covered by CNC.beforeCheckout below instead.
      form.addEventListener("submit", function () {
        if (autoOn) watchAdd();
      });

      var repaint = function () { setTimeout(paint, 0); };
      form.addEventListener("change", repaint);
      form.addEventListener("input", function (e) {
        if (e.target && e.target.name === "quantity") repaint();
      });
      form.addEventListener("click", function (e) {
        if (e.target.closest("[data-qty-step], [data-option-value]")) repaint();
      });
    }
    document.addEventListener("cnc:buyface", function () { setTimeout(paint, 0); });

    /* Apply / Switch. With a bag: straight onto the cart (applyCoupon keeps
       any points code and replaces the coupon). Without one: it becomes the
       code that auto-applies on Add to Bag. A refusal is shown here, in the
       customer's words — they asked for this one. */
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-offer-apply]");
      if (!btn || S.busy) return;
      var code = btn.getAttribute("data-offer-apply");
      var prev = bagCode(S.cart);
      S.error = "";
      S.chosen = code;
      var r = offerRec(); r.chosen = code; r.declined = false; offerSave(r);

      if (!(S.cart && S.cart.item_count)) { paint(); return; }

      S.busy = true;
      btn.disabled = true;
      btn.textContent = prev ? "Switching…" : "Applying…";
      window.CNC.applyCoupon(code).then(function (res) {
        if (res && res.ok) {
          var r2 = offerRec(); r2.applied = code; offerSave(r2);
          S.cart = res.cart;
          return;
        }
        S.chosen = "";
        S.error = (res && res.reason) || (code + " can't be applied to this bag.");
        // Don't leave the customer worse off than before they tapped.
        if (prev) {
          return window.CNC.applyCoupon(prev).then(function (back) {
            if (back && back.cart) S.cart = back.cart;
            S.error += " " + prev + " is still applied.";
          });
        }
        if (res && res.cart) S.cart = res.cart;
      }).catch(function () {
        S.chosen = "";
        S.error = "Couldn't reach your bag just then. Please try again.";
      }).then(function () { S.busy = false; paint(); });
    });

    if (window.CNC.onCartChange) {
      window.CNC.onCartChange(function () {
        if (S.busy) return;
        window.CNC.fetchCart(true).then(function (c) { if (c) { S.cart = c; paint(); } });
      });
    }

    if (autoOn) window.CNC.beforeCheckout = autoApply;

    /* Cart first — same origin, fast — so even the instant paint knows
       whether the bag already carries a coupon. Then the cached list (if
       any) paints at once, and the live list repaints when it lands. */
    var cartReady = window.CNC.fetchCart().then(function (c) {
      S.cart = c || null;
      var rec = offerRec();
      if (S.cart && !S.cart.item_count && (rec.applied || rec.declined)) {
        offerSave({ chosen: rec.chosen || "" });   // fresh bag, fresh start
      }
    }, function () {});

    var cached = cachedCoupons();
    if (cached) {
      cartReady.then(function () {
        if (S.coupons) return;                      // live beat us to it
        S.coupons = cached;
        paint();
      });
    }

    Promise.all([window.CNC.coupons(), cartReady])
      .then(function (res) {
        var live = Array.isArray(res[0]) ? res[0] : [];
        if (live.length) {
          cacheCoupons(live);
          S.coupons = live;
        } else if (!S.coupons) {
          S.coupons = [];
        }
        if (!S.coupons.length && !bagCode(S.cart)) {
          offersReady(root, true);                  // feed down: editor blocks
          return;
        }
        paint();
      })
      .catch(function (e) {
        console.warn("[cnc-offers] live offers unavailable — keeping the editor's blocks", e);
        offersReady(root, true);
      });
  }

  /* ═══ Product information tabs ════════════════════════════════════════════ */

  function initTabs(root) {
    if (!once(root, "cncTabs")) return;

    var rail   = root.querySelector("[data-tabrail]");
    var wrap   = root.querySelector("[data-tabrail-wrap]");
    var ink    = root.querySelector("[data-tab-ink]");
    var tabs   = Array.prototype.slice.call(root.querySelectorAll("[data-tab]"));
    var panels = Array.prototype.slice.call(root.querySelectorAll("[data-tabpanel]"));
    if (!rail || tabs.length === 0 || tabs.length !== panels.length) return;

    var deepLink = root.getAttribute("data-tabs-deeplink") === "true";
    var current  = -1;

    function moveInk(instant) {
      if (!ink) return;
      var btn = tabs[current];
      if (!btn) return;
      if (instant) ink.classList.add("is-instant");
      // Both axes: the rail wraps to a second line on a desktop column, and a
      // pill that only tracked x would slide along the wrong row.
      ink.style.setProperty("--ink-x", btn.offsetLeft + "px");
      ink.style.setProperty("--ink-y", btn.offsetTop + "px");
      ink.style.setProperty("--ink-w", btn.offsetWidth + "px");
      ink.style.setProperty("--ink-h", btn.offsetHeight + "px");
      if (instant) {
        // Flush the no-transition frame before handing animation back, or the
        // class removal lands in the same frame and the pill flies in anyway.
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { ink.classList.remove("is-instant"); });
        });
      }
    }

    function syncFades() {
      if (!wrap) return;
      var max = rail.scrollWidth - rail.clientWidth;
      wrap.classList.toggle("has-start", rail.scrollLeft > 4);
      wrap.classList.toggle("has-end", max > 4 && rail.scrollLeft < max - 4);
    }

    function select(i, opts) {
      opts = opts || {};
      if (i < 0 || i >= tabs.length || i === current) {
        if (i === current && opts.focus) tabs[i].focus();
        return;
      }
      var first = current === -1;
      current = i;

      tabs.forEach(function (t, n) {
        var on = n === i;
        t.setAttribute("aria-selected", on ? "true" : "false");
        // Roving tabindex: one stop for the whole rail, arrows move within it.
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p, n) { p.classList.toggle("is-active", n === i); });

      moveInk(first);

      // Centre the chosen tab when the rail scrolls, so the neighbours either
      // side stay discoverable instead of the selection hugging an edge.
      if (rail.scrollWidth > rail.clientWidth + 4) {
        var btn = tabs[i];
        rail.scrollTo({
          left: btn.offsetLeft - (rail.clientWidth - btn.offsetWidth) / 2,
          behavior: first ? "auto" : behavior()
        });
      }

      measureClamps(panels[i]);
      if (opts.focus) tabs[i].focus();

      if (deepLink && !first && window.history && window.history.replaceState) {
        var key = tabs[i].dataset.tabKey;
        if (key) {
          try { window.history.replaceState(null, "", "#" + key); } catch (e) { /* file:// */ }
        }
      }
    }

    tabs.forEach(function (t, i) {
      t.addEventListener("click", function (e) { e.preventDefault(); select(i); });
    });

    rail.addEventListener("keydown", function (e) {
      var map = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 };
      if (e.key === "Home") { e.preventDefault(); return select(0, { focus: true }); }
      if (e.key === "End")  { e.preventDefault(); return select(tabs.length - 1, { focus: true }); }
      var step = map[e.key];
      if (!step) return;
      e.preventDefault();
      // Wrap around — a tab strip is a ring, not a line with two dead ends.
      var next = (current + step + tabs.length) % tabs.length;
      select(next, { focus: true });
    });

    rail.addEventListener("scroll", syncFades, { passive: true });

    if ("ResizeObserver" in window) {
      new ResizeObserver(function () { moveInk(true); syncFades(); }).observe(rail);
    } else {
      window.addEventListener("resize", function () { moveInk(true); syncFades(); });
    }
    // Web fonts land after first paint and change every tab's width with them.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { moveInk(true); syncFades(); });
    }

    root.classList.add("is-ready");

    /* Opening tab: a deep link wins, otherwise the first one. The key is
       matched against a handleised label, so /products/x#care-safety lands a
       support reply straight on the right panel. */
    var start = 0;
    if (deepLink) {
      var want = (window.location.hash || "").replace(/^#/, "").toLowerCase();
      if (!want) {
        var qs = window.location.search.match(/[?&]tab=([^&]+)/);
        if (qs) want = decodeURIComponent(qs[1]).toLowerCase();
      }
      if (want) {
        tabs.forEach(function (t, i) {
          if ((t.dataset.tabKey || "").toLowerCase() === want) start = i;
        });
      }
    }
    select(start);
    syncFades();

    root.__cncSelectTab = function (key) {
      tabs.forEach(function (t, i) {
        if ((t.dataset.tabKey || "") === key) select(i);
      });
    };
    root.__cncSelectPanel = function (el) {
      panels.forEach(function (p, i) { if (p === el || p.contains(el)) select(i); });
    };
  }

  /* ═══ Long copy ═══════════════════════════════════════════════════════════
     Measured only when the panel is actually on screen — a hidden panel has a
     scrollHeight of 0, so measuring at boot would clamp nothing and show a
     "Read more" on a two-line paragraph. */

  function measureClamps(scope) {
    if (!scope) return;
    Array.prototype.forEach.call(scope.querySelectorAll("[data-clamp]"), function (el) {
      if (el.dataset.clampDone === "1") return;
      var limit = parseInt(el.dataset.clamp, 10) || 240;
      if (!el.offsetParent && !el.getClientRects().length) return;  // still hidden
      el.dataset.clampDone = "1";
      if (el.scrollHeight <= limit + 32) return;

      el.style.setProperty("--clamp-h", limit + "px");
      el.classList.add("is-clamped");

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cnc-more";
      btn.setAttribute("aria-expanded", "false");
      btn.innerHTML = '<span>Read more</span>' +
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
        'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<polyline points="6 9 12 15 18 9"></polyline></svg>';

      btn.addEventListener("click", function () {
        var open = el.classList.toggle("is-clamped") === false;
        btn.setAttribute("aria-expanded", String(open));
        btn.querySelector("span").textContent = open ? "Read less" : "Read more";
        if (!open) {
          var top = el.getBoundingClientRect().top + window.pageYOffset - 120;
          window.scrollTo({ top: top, behavior: behavior() });
        }
      });

      el.insertAdjacentElement("afterend", btn);
    });
  }

  /* ═══ Recommendation rail ═════════════════════════════════════════════════
     Only the FIRST paint. cnc-app.js owns both the click (a delegated handler
     on document) and every repaint after a scroll (a capture-phase scroll
     listener, also on document) — but nothing ever ran that painter on load, so
     the arrows sat at their server-rendered `hidden` and never appeared.

     Dispatching a scroll event is how this asks for that paint without owning a
     second copy of the rule for when an arrow is live. Capture-phase listeners
     reach the target whether or not the event bubbles, which is why a plain
     Event works here even though scroll doesn't bubble. */

  function paintRails() {
    document.querySelectorAll("[data-cnc-rail-track]").forEach(function (track) {
      track.dispatchEvent(new Event("scroll"));
    });
  }

  /* ═══ Q&A groups — one open at a time ═════════════════════════════════════ */

  function initQaGroup(group) {
    if (!once(group, "cncQaGroup")) return;
    var items = Array.prototype.slice.call(group.querySelectorAll(".cnc-qa"));
    items.forEach(function (item) {
      item.addEventListener("toggle", function () {
        if (!item.open) return;
        items.forEach(function (other) { if (other !== item) other.open = false; });
      });
    });
  }

  /* ═══ Back in stock ═══════════════════════════════════════════════════════
     The sold-out face of the buy bar. cnc-app.js decides WHEN this is on
     screen (setBuyFace, called from the same apply() that rewrites the price);
     everything here is what happens once it is.

     Three states, one container: the bell CTA → the email panel → the "you're
     on the list" receipt. They are siblings toggled with [hidden] rather than
     re-rendered, so the fixed bar never reflows to zero height mid-swap.

     TWO WAYS TO SEND, and the second one is the reason this ships as a theme
     change rather than waiting on a deploy.

       proxy  — POST /apps/cnc/addresses?resource=back-in-stock. Shopify signs
                the request and appends logged_in_customer_id, cnc-api writes
                the Firestore document, and the APP reads that same document.
                One waitlist, both surfaces, one restock send.

       native — if that endpoint is not deployed yet the proxy answers with a
                themed 404 page, not JSON. Rather than lose the customer we
                post Shopify's own customer form instead, tagged with the
                product and variant, so the lead lands in Customers where a
                segment can still reach it. Shopify bounces back to this page
                with ?customer_posted=true and we paint the receipt.

     The probe is the panel-open GET, not a page-load call: it costs nothing on
     the vast majority of PDPs that are in stock, and doing it before the
     customer types is what stops them composing an email into a field that is
     about to navigate away.

     NO <form> ELEMENT HERE. The markup sits inside the product form, so the
     input is deliberately name-less (never submitted with an Add to Bag) and
     Enter is intercepted — without that, Enter in the email field submits the
     PRODUCT form and adds a sold-out variant to the bag. */

  var BIS_KEY = "cnc:bis";          /* variantId -> email, this browser */
  var BIS_SENT = "cnc:bis-pending"; /* variantId awaiting the native round trip */

  function bisAll() {
    try { return JSON.parse(localStorage.getItem(BIS_KEY) || "{}") || {}; }
    catch (e) { return {}; }
  }

  function bisRemember(variantId, email) {
    try {
      var all = bisAll();
      all[String(variantId)] = email;
      localStorage.setItem(BIS_KEY, JSON.stringify(all));
    } catch (e) { /* private mode — the receipt just won't survive a reload */ }
  }

  /* An unconfigured App Proxy answers with the theme's 404 PAGE. res.json() on
     that throws a parse error that reads like a bug in this file, so read the
     text and let null mean "no API here". Same helper, same reason, as
     cnc-account.js. */
  function bisReadJson(res) {
    return res.text().then(function (body) {
      if (!body) return null;
      try { return JSON.parse(body); } catch (e) { return null; }
    });
  }

  /* Deliberately loose. The server validates for real; this only exists to
     catch the typo before a round trip, and a regex strict enough to argue
     with RFC 5322 rejects addresses that genuinely deliver. */
  function bisValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || "").trim());
  }

  function initBackInStock(root) {
    if (!once(root, "cncBis")) return;

    var cta   = root.querySelector("[data-bis-open]");
    var panel = root.querySelector("[data-bis-panel]");
    var done  = root.querySelector("[data-bis-done]");
    var input = root.querySelector("[data-bis-input]");
    var send  = root.querySelector("[data-bis-send]");
    var close = root.querySelector("[data-bis-close]");
    var msg   = root.querySelector("[data-bis-msg]");
    var at    = root.querySelector("[data-bis-at]");
    if (!cta || !panel || !done || !input || !send) return;

    var endpoint = root.getAttribute("data-bis-endpoint") || "";
    var mode = "unknown";   /* unknown | proxy | native */
    var busy = false;

    function variantId() { return root.getAttribute("data-bis-variant") || ""; }

    function say(text, kind) {
      if (!msg) return;
      msg.textContent = text || "";
      msg.hidden = !text;
      msg.setAttribute("data-kind", kind || "error");
    }

    function show(which) {
      cta.hidden   = which !== "cta";
      panel.hidden = which !== "panel";
      done.hidden  = which !== "done";
      cta.setAttribute("aria-expanded", which === "panel" ? "true" : "false");
    }

    function receipt(email) {
      if (at) at.textContent = email;
      show("done");
    }

    /* Called on load and again whenever cnc-app.js swaps in another variant:
       the waitlist is per variant, so Blue being signed up says nothing about
       Red and the panel has to go back to its closed state. */
    function sync() {
      say("");
      /* Only PREFILL an empty field. A guest who typed their address and then
         tapped another colour has not changed their mind about the address,
         and retyping it is the kind of small insult that loses the signup. */
      if (!input.value) input.value = root.getAttribute("data-bis-email") || "";
      var known = bisAll()[variantId()];
      if (known) receipt(known);
      else show("cta");
    }

    /* One GET when the panel opens. Answers two questions at once: is this
       customer already on the list for this variant, and does the endpoint
       exist at all. */
    function probe() {
      if (mode !== "unknown" || !endpoint) { if (!endpoint) mode = "native"; return; }
      var url = endpoint + "&variant=" + encodeURIComponent(variantId());
      fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(bisReadJson)
        .then(function (data) {
          if (!data) { mode = "native"; return; }   /* proxy absent */
          mode = "proxy";
          if (data.subscribed && data.email) {
            bisRemember(variantId(), data.email);
            receipt(data.email);
          }
        })
        .catch(function () { mode = "native"; });
    }

    /* Shopify's own customer form, built here rather than rendered into the
       page: a <form> inside the product form is not parseable HTML, so this
       one is created at submit time, appended to <body> and posted from
       there. The tags are what make the lead reachable — a Customers segment
       on bis-<handle> is the manual version of the restock send. */
    function nativeSend(email) {
      var f = document.createElement("form");
      f.method = "post";
      f.action = "/contact#cnc-bis";
      f.acceptCharset = "UTF-8";
      f.style.display = "none";

      function field(name, value) {
        var i = document.createElement("input");
        i.type = "hidden"; i.name = name; i.value = value;
        f.appendChild(i);
      }
      field("form_type", "customer");
      field("utf8", "✓");
      field("contact[email]", email);
      field("contact[tags]", [
        "back-in-stock",
        "bis-" + (root.getAttribute("data-bis-handle") || ""),
        "bis-v-" + variantId(),
      ].join(", "));

      /* Written BEFORE the navigation, read after it — this is the only thread
         that survives the round trip, and it is what lets the page come back
         showing the receipt instead of the bell again. */
      bisRemember(variantId(), email);
      try { sessionStorage.setItem(BIS_SENT, variantId()); } catch (e) {}

      document.body.appendChild(f);
      f.submit();
    }

    function submit() {
      if (busy) return;
      var email = String(input.value || "").trim();
      if (!bisValidEmail(email)) {
        say("That doesn't look like an email address.");
        input.focus();
        return;
      }

      if (mode === "native") { nativeSend(email); return; }

      busy = true;
      say("");
      send.disabled = true;
      send.setAttribute("data-busy", "1");

      fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          variantId: variantId(),
          productId: root.getAttribute("data-bis-product") || "",
          handle:    root.getAttribute("data-bis-handle") || "",
          title:     root.getAttribute("data-bis-title") || "",
          email:     email,
        }),
      })
        .then(bisReadJson)
        .then(function (data) {
          /* No JSON means the proxy isn't there. Don't show an error for
             something the customer cannot fix — take the native path and let
             the navigation be the answer. */
          if (!data) { mode = "native"; nativeSend(email); return; }
          if (!data.ok) throw new Error(data.error || "That didn't save.");
          mode = "proxy";
          bisRemember(variantId(), email);
          receipt(email);
        })
        .catch(function (err) {
          say((err && err.message) || "That didn't reach us — check your connection and try again.");
        })
        .then(function () {
          busy = false;
          send.disabled = false;
          send.removeAttribute("data-busy");
        });
    }

    cta.addEventListener("click", function () {
      show("panel");
      probe();
      /* iOS only raises the keyboard for a focus inside the gesture that
         opened the field, so this cannot wait for the probe. */
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    });

    if (close) close.addEventListener("click", function () { show("cta"); cta.focus(); });
    send.addEventListener("click", submit);

    /* Enter would otherwise submit the PRODUCT form this markup lives inside,
       which on a sold-out variant means a failed add and a lost email. */
    input.addEventListener("keydown", function (e) {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();
      submit();
    });
    input.addEventListener("input", function () { say(""); });

    root.__cncBisSync = sync;
    sync();

    /* Back from the native round trip: Shopify returns to this page with
       ?customer_posted=true and no other trace of what was signed up for. */
    var posted = /[?&]customer_posted=true/.test(window.location.search);
    if (posted) {
      var pending = null;
      try { pending = sessionStorage.getItem(BIS_SENT); } catch (e) {}
      if (pending && pending === variantId()) {
        try { sessionStorage.removeItem(BIS_SENT); } catch (e) {}
        receipt(bisAll()[pending] || "");
      }
    }
  }

  /* cnc-app.js swapped the buy bar to another variant. Re-read the waitlist
     for the new one — this listener is bound once, at module level, because
     the event is fired on `document`. */
  document.addEventListener("cnc:buyface", function (e) {
    var root = e.detail && e.detail.root;
    if (root && root.__cncBisSync) root.__cncBisSync();
  });

  /* ═══ Boot ════════════════════════════════════════════════════════════════ */

  function boot() {
    document.querySelectorAll("[data-stage]").forEach(initStage);
    // After the stage: initOptionSync reaches for the goTo it publishes.
    document.querySelectorAll("[data-product-form]").forEach(initOptionSync);
    document.querySelectorAll("[data-tabs]").forEach(initTabs);
    document.querySelectorAll("[data-qa-single]").forEach(initQaGroup);
    document.querySelectorAll("[data-bis]").forEach(initBackInStock);
    initCoupons();
    /* After cnc-app.js: it publishes CNC.coupons / applyCoupon, and `defer`
       keeps document order — but a section reload can re-run this first, so
       initBestOffers bails quietly if they aren't there yet and keeps the
       server-rendered tickets. */
    document.querySelectorAll("[data-cnc-best-offers]").forEach(initBestOffers);
    syncStickyTop();
    paintRails();
  }

  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { syncStickyTop(); paintRails(); }, 150);
  });
  // Cards settle once their images have dimensions, which can change whether
  // the rail overflows at all.
  window.addEventListener("load", function () { syncStickyTop(); paintRails(); });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Theme editor: re-bind after a section reload, and follow the merchant's
  // selection into the matching tab so editing a hidden panel isn't blind.
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:block:select", function (e) {
    var panel = e.target && e.target.closest ? e.target.closest("[data-tabpanel]") : null;
    if (!panel) return;
    var root = panel.closest("[data-tabs]");
    if (root && root.__cncSelectPanel) root.__cncSelectPanel(panel);
  });

  // Small public hook so a link, a chat reply or another section can jump the
  // customer to a named tab: CNC.openProductTab('shipping-returns').
  window.CNC = window.CNC || {};
  window.CNC.openProductTab = function (key) {
    document.querySelectorAll("[data-tabs]").forEach(function (root) {
      if (root.__cncSelectTab) root.__cncSelectTab(key);
    });
  };
})();
